import * as React from 'react';
import { Tools } from '../Tools';
import { UpdateMode, Version } from '../Model';
import { Note } from '../Components';
import { Label, NotificationAlert, NotificationType, TrustpilotSpinner } from '../CommonUI';
import Button from '../CommonUI/Control/Button';
import { Box, Flex } from '../CommonUI/Layout';
import { css, StyleSheet } from 'aphrodite';
import { getReleases } from '../Api/settingsApiClient';
import { LinkText, Text } from 'ilenia';
import { Common } from '../Assets/styles/common';
import { Utils } from '../Utils';

interface UpdateProps {
    pluginVersion: string;
    source: string;
    version: string;
    isFromMarketplace?: string;
    sendPostMessage: (message: string) => void;
    tierName: string;
}

interface UpdateState {
    updatingVersion: string;
    availableReleases: any;
    latestVersionAvailable: string;
    latestNotes: string[];
    pleaseRefresh: boolean;
}

const platformPlaceholder = ':platform';

export class Update extends React.Component<UpdateProps, UpdateState> {
    public interval: NodeJS.Timer | null;

    constructor(props: UpdateProps) {
        super(props);
        this.state = {
            availableReleases: undefined,
            latestVersionAvailable: '',
            latestNotes: [],
            updatingVersion: '',
            pleaseRefresh: false,
        };
        this.downloadPlugin = this.downloadPlugin.bind(this);
    }

    public componentDidMount() {
        getReleases(this.props.source)
        .then((result) => {
            const releases: any = [];
            let latestVersion = '';
            let latestNotes: string[] = [];
            if (result && Array.isArray(result)) {
                result.forEach((element) => {
                    if (element) {
                        if (Tools.compareVersions(element.version, this.props.pluginVersion, '>')) {
                            releases.push(element);
                        }
                        if (element.latestVersion) {
                            latestVersion = element.version;
                            latestNotes = element.notes;
                        }
                    }
                });
            }
            this.setState({ availableReleases: releases, latestVersionAvailable: latestVersion, latestNotes });
        });
        analytics.page();
    }

    public componentWillReceiveProps(nextProps: UpdateProps) {
        const { pluginVersion } = nextProps;
        const { latestVersionAvailable } = this.state;

        if (Tools.compareVersions(pluginVersion, latestVersionAvailable, '>=')) {
            if (this.interval) {
                clearInterval(this.interval);
            }
            this.setState({ updatingVersion: '', pleaseRefresh: true });
        }
    }

    public componentWillUnmount() {
        if (this.interval) {
            clearInterval(this.interval);
            this.interval = null;
        }
    }

    public getPluginUrl(source: string) {
        const url = process.env.REACT_APP_PluginUrl;
        if (url) {
            return url.replace(platformPlaceholder, source.toLowerCase());
        }
        return '';
    }

    public getOpencartVersion(version: string) {
        version = version.replace('OpenCart-', '');
        if (Tools.compareVersions(version, '3.0.0.0', '>=')) {
            return 'v3';
        } else if (Tools.compareVersions(version, '2.3.0.0', '>=')) {
            return 'v2.3';
        } else {
            return 'v2';
        }
    }

    public updateMode() {
        const { source, pluginVersion, isFromMarketplace } = this.props;
        switch (source.toLowerCase()) {
            case 'woocommerce':
                if (isFromMarketplace === 'true' || Tools.compareVersions(pluginVersion, '1.0.525', '<=')) {
                    return UpdateMode.DownloadZip;
                }
                return UpdateMode.AutomaticUpdate;
            case 'prestashop':
            case 'magento1':
                return UpdateMode.AutomaticUpdate;
            default:
                return UpdateMode.DownloadZip;
        }
    }

    public downloadPlugin() {
        const { source, sendPostMessage, version } = this.props;
        const { latestVersionAvailable } = this.state;
        switch (this.updateMode()) {
            case UpdateMode.DownloadZip:
                if (source.toLowerCase() === 'magento2') {
                    window.open(process.env.REACT_APP_MarketplaceMagento2Url, '_blank');
                    break;
                }
                let url = '';
                url = source.toLowerCase() === 'opencart'
                    ? `${this.getPluginUrl(source)}/${this.getOpencartVersion(version)}/trustpilot.opencart.ocmod.zip`
                    : `${this.getPluginUrl(source)}/trustpilot.zip`;
                window.location.replace(url);
                break;
            case UpdateMode.AutomaticUpdate:
                this.setState({ updatingVersion: latestVersionAvailable }, () => {
                    this.interval = setInterval(() => {
                        sendPostMessage('reload');
                    }, 10000);
                    sendPostMessage('update');
                });
                break;
        }
    }

    public getButtonText(updateMode: UpdateMode) {
        const textId = updateMode === UpdateMode.DownloadZip ?
                'updateButtonDownload' :
                'updateButtonUpdate';

        return `Containers.${textId}`;
    }

    public getUnderLabelText(source: string) {
        return `Containers.${
            source.toLowerCase() === 'magento2' ?
                'updateSupportArticleOrStepsMagento2' :
                'updateSupportArticleOrSteps'
            }`;
    }

    public render() {
        const { source, pluginVersion, tierName } = this.props;
        const { availableReleases, latestVersionAvailable, latestNotes, updatingVersion, pleaseRefresh } = this.state;

        if (pleaseRefresh) {
            NotificationAlert.alert(NotificationType.Warning, 'Containers.updateRefreshnotification', true, '', 0);
        }

        if (updatingVersion || !availableReleases) {
            return <TrustpilotSpinner active={true} height="500px"/>;
        }
        const interpolationsLatestVersion = { version: pluginVersion, pluginVersion };
        const interpolationsUpdate = { version: latestVersionAvailable, pluginVersion };
        let previousVersionsCount = 0;
        const previousVersions = availableReleases.map((version: Version, vindex: number) => {
            if (Tools.compareVersions(pluginVersion, version.version, '<') && version.version !== latestVersionAvailable) {
                previousVersionsCount++;
                return (
                    <Box key={`node_${vindex}`}>
                        <h4>{version.version}</h4>
                        <ul>
                            {version.notes.map((note: string, nindex: number) =>
                                <li key={`node_${nindex}`}>{note}</li>
                            )}
                        </ul>
                    </Box>
                );
            } else {
                return undefined;
            }
        });
        const previousVersionsLabel = (previousVersionsCount > 0)
            ? <Label label="Containers.updatePreviousVersion" />
            : undefined;
        const updateMode = this.updateMode();
        const appPlatforms = ['shopify', 'bigcommerce'];
        const upToDateLabel = (appPlatforms.indexOf(source.toLowerCase()) > -1) ? 'Containers.appUpToDate' : 'Containers.pluginUpToDate';

        if (Tools.compareVersions(pluginVersion, latestVersionAvailable, '>=')) {
            return (
                <div className={css(styles.container)}>
                    <Flex>
                        <Box>
                            <Label label={upToDateLabel}/>
                        </Box>
                            <Note>
                                <Label
                                    label="Containers.updatedPluginVersion"
                                    interpolations={interpolationsLatestVersion}
                                    className={css(styles.greyStyle, styles.fontStyle)}
                                />
                            </Note>
                            <b>
                                <Label label="Containers.whatsNew" className={css(styles.fontStyle)}/>
                            </b>
                            <ul className={css(styles.listStyle)}>
                                {latestNotes.map((note: string, nindex: number) =>
                                    <li className={css(styles.fontStyle, styles.itemStyle)} key={`node_${nindex}`}>
                                        <span>{note}</span>
                                    </li>
                                )}
                            </ul>
                    </Flex>
                </div>
            );
        } else {
            return (
                <div className={css(styles.container)}>
                    <Flex>
                        <Label
                            label="Containers.updateVersionUpgrade"
                            interpolations={interpolationsUpdate}
                        />
                        <span className={css(styles.underLabel)}>
                            <Text id="Containers.updateList" />
                        </span>
                        <ul className={css(styles.ul)}>
                            {latestNotes.map((note: string, nindex: number) =>
                                <li key={`node_${nindex}`}>{note}</li>
                            )}
                        </ul>
                        { updateMode === UpdateMode.DownloadZip ?
                            <React.Fragment>
                                <Label label="Containers.updateHowTo" />
                                <span className={css(styles.underLabel, styles.link)}>
                                    <LinkText links={[{href: Utils.getSupportArticleUrl(source), target: '_blank' }]} id={this.getUnderLabelText(source)}/>
                                </span>
                                {source.toLowerCase() === 'magento2' ? null :
                                    <ul className={css(styles.ul)}>
                                        <li><Text id="Containers.updateStep1"/></li>
                                        <li><Text id="Containers.updateStep2"/></li>
                                        <li><Text id="Containers.updateStep3"/></li>
                                    </ul>
                                }
                            </React.Fragment>
                        : null}
                        {['magento2', 'shopify', 'bigcommerce'].includes(source.toLowerCase()) ? null :
                            <Button
                                track={{
                                    placement: 'bottom left corner',
                                    action: 'Update version button clicked',
                                    name: 'update-plugin-button',
                                    source,
                                    tierName,
                                }}
                                onClick={this.downloadPlugin}
                                pl={0}
                                mt={10}
                            >
                                <Text
                                    id={this.getButtonText(updateMode)}
                                />
                            </Button>
                        }
                        <Box>
                            {previousVersionsLabel}
                            {previousVersions}
                        </Box>
                    </Flex>
                </div>
            );
        }
    }
}

const styles = StyleSheet.create({
    container: {
        maxWidth: '532px',
        marginTop: '14px',
    },
    greyStyle: {
        color: '#a9a9a9',
        lineHeight: '24px',
    },
    fontStyle: {
        fontSize: '14px',
        fontStyle: 'normal',
    },
    itemStyle: {
        paddingBottom: '5px',
    },
    listStyle: {
        marginTop: 0,
    },
    ul: {
        marginBottom: '15px',
        paddingInlineStart: '25px',
    },
    underLabel: {
        marginTop: '6px',
    },
    button: {
        marginTop: '10px',
        paddingLeft: 0,
    },
    link: {
        ':nth-child(1n) > a': {
            font: Common.Font.normalText,
            fontStretch: 'normal',
            lineHeight: '1.43',
            letterSpacing: 'normal',
            color: '#368cd7',
            textDecoration: 'none'
        },
        ':nth-child(1n) > a:hover': {
            color: '#2471b3',
            cursor: 'pointer'
        },
        ':nth-child(1n) > a:focus': {
            borderRadius: '2px',
            border: 'solid 2px #54a5e5',
            outlineStyle: 'none'
        }
    },
});



// WEBPACK FOOTER //
// ./src/Containers/Update.tsx